import { getNation } from '@brig/nations';
import { flagArt } from '@brig/nations/flags';
import type { ShipClassId } from '@brig/rules-engine';
import { palette } from '@brig/theme/tokens';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n/index.ts';
import { NationFlag } from '../nations/NationFlag.tsx';
import {
  MIN_RASTER_TOKEN_PX,
  SHIP_ART,
  effectiveArt,
  flagPoints,
  tokenAspect,
  tokenUrl,
  type FlagPoint,
  type ShipArt,
} from './art.ts';
import type { Facing } from './sides.ts';
import * as css from './ShipToken.css.ts';

export interface ShipTokenState {
  selected?: boolean;
  canMove?: boolean;
  targetable?: boolean;
  inVolley?: boolean;
  /** Holes taken; drawn as pips under the hull. */
  damage?: number;
  /** Holes that sink this class; only used for the accessible label. */
  sinksAt?: number;
  repairing?: boolean;
  anchored?: boolean;
  sunk?: boolean;
}

export interface ShipTokenProps {
  type: ShipClassId;
  /** Nation id (see @brig/nations): its flag flies from the ship. */
  nation: string;
  /** Rendered size in CSS px (the token is square). */
  size?: number;
  state?: ShipTokenState;
  facing?: Facing;
  /** Overrides VITE_SHIP_ART, e.g. to compare both layers in the gallery. */
  art?: ShipArt;
  /** Soft neutral shadow under raster ships, for legibility on the sea. */
  shadow?: boolean;
  /** The nation's flag at the main-mast top. Off for the second ship in a shared cell. */
  flag?: boolean;
  /** Position and size in the parent's user units when nested inside another <svg> (the board). */
  x?: number;
  y?: number;
  box?: number;
  className?: string;
}

const VIEW = 32;

/**
 * A ship on the map. In `source` art mode (and at ≥32 px) it shows the cut-out illustration
 * over a soft neutral shadow; otherwise our own vector token from <ShipSprite>, which must be
 * mounted once per page.
 */
export function ShipToken({
  type,
  nation,
  size = 32,
  state = {},
  facing = 'east',
  art = SHIP_ART,
  shadow = true,
  flag = true,
  x,
  y,
  box,
  className,
}: ShipTokenProps) {
  const { t } = useTranslation();
  const raster = effectiveArt(art) === 'source' && size >= MIN_RASTER_TOKEN_PX;
  const label = [
    t('ships.token.label', {
      ship: t(`ships.names.${type}`),
      nation: getNation(nation).names[currentLocale()],
    }),
    state.damage && state.sinksAt
      ? t('ships.token.damage', { damage: state.damage, max: state.sinksAt })
      : null,
    state.anchored ? t('ships.token.anchored') : null,
    state.repairing ? t('ships.token.repairing') : null,
    state.sunk ? t('ships.token.sunk') : null,
  ]
    .filter(Boolean)
    .join(', ');
  const mirror = facing === 'west' ? `translate(${VIEW} 0) scale(-1 1)` : undefined;

  return (
    <svg
      role="img"
      aria-label={label}
      width={box ?? size}
      height={box ?? size}
      x={x}
      y={y}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      className={[css.token, className].filter(Boolean).join(' ')}
      style={{ '--nation-color': getNation(nation).accent.primary } as CSSProperties}
      data-ship={type}
      data-nation={nation}
      data-art={raster ? 'source' : 'fallback'}
    >
      {state.selected ? (
        <rect
          x={0.75}
          y={0.75}
          width={30.5}
          height={30.5}
          rx={3}
          fill="none"
          stroke={palette.gold}
          strokeWidth={1.5}
        />
      ) : state.canMove ? (
        <rect
          x={0.75}
          y={0.75}
          width={30.5}
          height={30.5}
          rx={3}
          fill="none"
          stroke={palette.success}
          strokeWidth={1}
          strokeDasharray="2.5 2"
        />
      ) : null}
      {state.inVolley ? (
        <ellipse cx={16} cy={27} rx={14.5} ry={3} fill="none" stroke={palette.gold} strokeWidth={1.4} />
      ) : null}
      <g className={state.sunk ? css.sunk : undefined}>
        <g transform={mirror}>
          {raster ? (
            <RasterShip type={type} size={size} shadow={shadow} />
          ) : (
            <use href={`#ship-${type}`} width={VIEW} height={VIEW} />
          )}
        </g>
        {flag ? <ShipFlag type={type} nation={nation} size={size} raster={raster} facing={facing} /> : null}
      </g>
      {state.targetable ? <TargetCorners /> : null}
      {state.damage ? <DamagePips count={state.damage} /> : null}
      {state.repairing ? (
        <>
          <Badge x={23}>
            <Anchor />
          </Badge>
          <Badge x={15}>
            <Hammer />
          </Badge>
        </>
      ) : state.anchored ? (
        <Badge x={23}>
          <Anchor />
        </Badge>
      ) : null}
    </svg>
  );
}

/**
 * One flag per ship: the nation's, at the main-mast top, flying aft. When the hull is mirrored
 * the anchor moves but the flag itself is not mirrored, so it never reads back to front. At
 * ≤ 40 px the flag is drawn a bit larger relative to the ship.
 */
function ShipFlag({
  type,
  nation,
  size,
  raster,
  facing,
}: {
  type: ShipClassId;
  nation: string;
  size: number;
  raster: boolean;
  facing: Facing;
}) {
  const art = flagArt(nation);
  const flagPx = size <= 40 ? Math.max(6, size * 0.2) : size * 0.14;
  const h = (flagPx * VIEW) / size;
  const w = (h * art.width) / art.height;
  const aspect = tokenAspect(type);
  const toView = ([px, py]: FlagPoint): [number, number] =>
    raster ? [px * VIEW, VIEW / 2 - VIEW / 2 / aspect + (py * VIEW) / aspect] : [px * VIEW, py * VIEW];
  const [ax, ay] = toView(flagPoints(type, raster).main);
  const west = facing === 'west';
  const hoist = west ? VIEW - ax : ax;
  return (
    <NationFlag
      nation={nation}
      height={flagPx}
      box={h}
      x={west ? hoist : hoist - w}
      y={ay + 0.2}
      waving
      reversed={!west}
      decorative
    />
  );
}

function RasterShip({ type, size, shadow }: { type: ShipClassId; size: number; shadow: boolean }) {
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  // The ship is letterboxed into the square image: full width, height = width / aspect.
  const keel = VIEW / 2 + VIEW / 2 / tokenAspect(type);
  return (
    <>
      {shadow ? (
        // Neutral, soft and faint: lifts the hull off the sea without saying anything about sides.
        <ellipse
          cx={16}
          cy={keel - 1}
          rx={14}
          ry={2.2}
          fill={palette.ink}
          opacity={0.28}
          filter="url(#ship-shadow-blur)"
        />
      ) : null}
      <image
        href={tokenUrl(type, size, dpr)}
        width={VIEW}
        height={VIEW}
        preserveAspectRatio="xMidYMid meet"
      />
    </>
  );
}

function TargetCorners() {
  return (
    <path
      d="M1 7V1H7M25 1H31V7M31 25V31H25M7 31H1V25"
      fill="none"
      stroke={palette.sealRed}
      strokeWidth={2}
      strokeLinecap="square"
    />
  );
}

function DamagePips({ count }: { count: number }) {
  const gap = 3.6;
  const start = 16 - ((count - 1) * gap) / 2;
  return (
    <g fill={palette.ink} stroke={palette.paper} strokeWidth={0.8}>
      {Array.from({ length: count }, (_, i) => (
        <circle key={i} cx={start + i * gap} cy={30} r={1.5} />
      ))}
    </g>
  );
}

function Badge({ x, children }: { x: number; children: ReactNode }) {
  return (
    <g transform={`translate(${x} 0)`}>
      <circle cx={4.5} cy={4.5} r={4.2} fill={palette.paper} stroke={palette.ink} strokeWidth={0.6} />
      <g fill="none" stroke={palette.ink} strokeWidth={0.9} strokeLinecap="round">
        {children}
      </g>
    </g>
  );
}

const Anchor = () => <path d="M4.5 2.4V7.3M3 3.6H6M2.2 5.6Q4.5 8.6 6.8 5.6" />;
const Hammer = () => <path d="M2.6 7L5.4 4.2M3.9 2.5L6.6 5.2" strokeWidth={1.3} />;
